import React, { useEffect, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment, { now } from 'moment-timezone';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import _, { isEqual, uniqBy } from 'lodash';
import { replace } from 'connected-react-router';

import { PageHeader } from '../../../components/PageHeader';
import DashboardEditModeButton from '../../Dashboards/DashboardEditModeButton';
import DashboardFilter from '../../Dashboards/DashboardFilter';
import { Button } from '../../../components/PageHeader/Button';
import DashboardLayout from '../../Dashboards/DashboardLayout';
import Strings from '../../../Strings';
import { actions } from './redux/actions';
import { actions as orgActions } from '../../Organizations/redux/actions';
import { GET_MY_ORGS_RESULT, actions as orgFilterActions } from '../../../components/OrgSelect/orgFilterActions'
import { ORGANIZATION_LIST_RESULT } from '../../Organizations/redux/constants';
import { ColorPalette } from '../../Dashboards/DashboardColorPalettes';
import { actionsAdvancedFilters } from '../../../components/AdvancedFilters/actions';
import { openModalAction } from '../../../actions/modal';
import { isSuperUser } from '../../../utils/userRoles';
import AdvancedFilters, { Filter } from '../../../components/AdvancedFilters/AdvancedFilters';
import {
  CareProviderFilter,
  getFiltersDefinition,
  OrgFilter,
  OrgGroupFilter,
} from '../../../components/AdvancedFilters/FiltersDefinition';
import AdvancedFiltersBar from '../../../components/AdvancedFilters/AdvancedFiltersBar';
import { getRelevantFilters } from '../../../components/AdvancedFilters/helpers';
import {
  DASHBOARD_ID as ENROLLMENTS_DASHBOARD_ID,
  DEVICE_UTILIZATION_DASHBOARD_ID,
  getDefaultLayouts as getEnrollmentsDefaultLayouts,
  getDeviceUtilizationDefaultLayouts,
  getOverallDefaultLayouts,
  getOrganizationsDefaultLayouts,
  getShipmentsDefaultLayouts,
  ORGANIZATIONS_DASHBOARD_ID,
  OVERALL_DASHBOARD_ID,
  SHIPMENTS_DASHBOARD_ID as OPERATIONAL_DASHBOARD_ID,
} from './businessReportWidgetsMetadata';
import initializeDashboard from './businessReportWidgets';
import OperationalReportTab, {
  buildOperationalWidgets as buildHighlightsWidgets,
  buildPerformanceWidgets,
  getOperationalDefaultLayouts as getHighlightsDefaultLayouts,
  getPerformanceDefaultLayouts,
  OPERATIONAL_DASHBOARD_ID as HIGHLIGHTS_DASHBOARD_ID,
  PERFORMANCE_DASHBOARD_ID,
} from './BusinessReportHighlightsTab';
import { DateFilters } from '../../../utils/DateFilters';
import { apiCache } from '../../../utils/apiCache';
import { programsAndSubprograms } from '../../../utils/cmsPrograms';
import { Tab, Tabs } from '../../../components/Tabs';
import Select from '../../../components/Select';

const OVERALL_TAB = 'overall';
const PERFORMANCE_TAB = 'performance';
const ORGANIZATIONS_TAB = 'organizations';
const ENROLLMENTS_TAB = 'enrollments';
const OPERATIONAL_TAB = 'operational';
const DEVICE_UTILIZATION_TAB = 'device_utilization';
const HIGHLIGHTS_TAB = 'highlights';
const REPORT_TAB_ORDER = [OVERALL_TAB, PERFORMANCE_TAB, OPERATIONAL_TAB, HIGHLIGHTS_TAB, ENROLLMENTS_TAB, DEVICE_UTILIZATION_TAB, ORGANIZATIONS_TAB];
const REMOVED_REPORT_FILTER_GROUPS = ['org_administrative_state', 'org_business_state', 'org_operational_state'];

const ENROLLMENTS_BASE_RANGE_OPTIONS = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
];

const ENROLLMENTS_PERIOD_COUNT_OPTIONS = {
  day: [7, 14],
  week: [4, 8, 12],
  month: [3, 6, 12],
};

const getOperationalPeriodStart = (baseRange, anchor = moment(), timezone = moment.tz.guess()) => {
  const aligned = moment(anchor).tz(timezone);
  if (baseRange === 'day') {
    return aligned.startOf('day');
  }
  if (baseRange === 'month') {
    return aligned.startOf('month');
  }
  return aligned.startOf('isoWeek');
};

const stripOrganizationGroupsFromOperationalDataset = dataset => {
  if (!dataset) {
    return {};
  }

  const stripScope = item =>
    item?.scope
      ? {
          ...item,
          scope: _.omit(item.scope, 'organization_groups'),
        }
      : item;

  return {
    ...dataset,
    patients: (dataset.patients || []).map(stripScope),
    transitions: (dataset.transitions || []).map(stripScope),
    shipments: (dataset.shipments || []).map(stripScope),
    usage: (dataset.usage || []).map(stripScope),
  };
};

function ReportDashboard(props) {
  const {
    downloadActions,
    dashboardSettingsFeature,
    colorPalette,
    dashboardConfigurations,
    advancedFilters,
    getOrgs,
    getOrgsForFilter,
    getEnrollmentsSummary,
    getShipmentSummary,
    getDeviceUtilizationSummary,
    getOrganizationsSummary,
    getOperationalDataset,
    configDashboard,
    getOrganizationGroups,
    getCareProviders,
    updateAdvancedFilterState,
    timezone,
    replacePath,
  } = props;
  const effectiveTimezone = timezone || moment.tz.guess();
  const urlParams = new URLSearchParams(window.location.search);
  const tabFromUrl = urlParams.get('tab');

  const [widgets, setWidgets] = useState([]);
  const [activeTab, setActiveTab] = useState(tabFromUrl || OVERALL_TAB);
  const [enrollmentsBaseRange, setEnrollmentsBaseRange] = useState('month');
  const [enrollmentsPeriodsCount, setEnrollmentsPeriodsCount] = useState(12);
  const [refreshTimestamp, setRefreshTimestamp] = useState(now());
  const [widgetResizeTimestamps, setWidgetResizeTimestamps] = useState({});
  const [refreshEnable, setRefreshEnable] = useState(true);
  const [operationalBaseRange, setOperationalBaseRange] = useState('month');
  const [operationalGroupBy, setOperationalGroupBy] = useState('none');
  const [operationalPeriodStart, setOperationalPeriodStart] = useState(
    () => getOperationalPeriodStart('month', moment(), effectiveTimezone).format(),
  );

  const [orgs, setOrgs] = useReducer((state, newState) => ({ ...state, ...newState }), { loaded: false, data: [] });
  const [orgsForFilter, setOrgsForFilter] = useReducer((state, newState) => ({ ...state, ...newState }), { loaded: false, data: [] });
  const [orgGroups, setOrgGroups] = useState([]);
  const [careProviders, setCareProviders] = useState([]);
  const [enrollmentsSummary, setEnrollmentsSummary] = useReducer((state, newState) => ({ ...state, ...newState }), {
    loaded: false,
    data: {},
  });
  const [shipmentSummary, setShipmentSummary] = useReducer((state, newState) => ({ ...state, ...newState }), {
    loaded: false,
    data: {},
  });
  const [deviceUtilizationSummary, setDeviceUtilizationSummary] = useReducer((state, newState) => ({ ...state, ...newState }), {
    loaded: false,
    data: {},
  });
  const [organizationsSummary, setOrganizationsSummary] = useReducer((state, newState) => ({ ...state, ...newState }), {
    loaded: false,
    data: {},
  });
  const [operationalFacts, setOperationalFacts] = useReducer((state, newState) => ({ ...state, ...newState }), {
    loaded: false,
    data: {},
  });
  const request = React.useRef();
  const operationalRequest = React.useRef();
  const enrollmentPeriodsCountOptions = (ENROLLMENTS_PERIOD_COUNT_OPTIONS[enrollmentsBaseRange] || []).map(value => ({
    value,
    label: `${value}`,
  }));
  useEffect(() => {
    const allowedCounts = ENROLLMENTS_PERIOD_COUNT_OPTIONS[enrollmentsBaseRange] || [];
    if (allowedCounts.length > 0 && !allowedCounts.includes(enrollmentsPeriodsCount)) {
      setEnrollmentsPeriodsCount(allowedCounts[allowedCounts.length - 1]);
    }
  }, [enrollmentsBaseRange, enrollmentsPeriodsCount]);

  useEffect(() => {
    if (activeTab) {
      const newUrlParams = new URLSearchParams(window.location.search);
      newUrlParams.set('tab', activeTab);
      replacePath(`${window.location.pathname}?${newUrlParams}`);
    }
  }, [activeTab]);
  const currentDashboardId =
    activeTab === OVERALL_TAB
      ? OVERALL_DASHBOARD_ID
      : activeTab === HIGHLIGHTS_TAB
      ? HIGHLIGHTS_DASHBOARD_ID
      : activeTab === PERFORMANCE_TAB
      ? PERFORMANCE_DASHBOARD_ID
      : activeTab === ORGANIZATIONS_TAB
      ? ORGANIZATIONS_DASHBOARD_ID
      : activeTab === DEVICE_UTILIZATION_TAB
      ? DEVICE_UTILIZATION_DASHBOARD_ID
      : activeTab === OPERATIONAL_TAB
          ? OPERATIONAL_DASHBOARD_ID
          : ENROLLMENTS_DASHBOARD_ID;
  const configuration = dashboardConfigurations?.[currentDashboardId];

  useEffect(() => {
    if (activeTab === HIGHLIGHTS_TAB || activeTab === PERFORMANCE_TAB) {
      setWidgets(
        (activeTab === HIGHLIGHTS_TAB ? buildHighlightsWidgets : buildPerformanceWidgets)({
          operationalFacts: operationalFacts?.data,
          colorPalette,
          configuration,
          isLoading: !operationalFacts?.loaded,
          groupBy: operationalGroupBy,
        }),
      );
      return;
    }

    initializeDashboard(
      activeTab,
      enrollmentsSummary,
      shipmentSummary,
      deviceUtilizationSummary,
      organizationsSummary,
      orgs,
      setWidgets,
      configuration,
      colorPalette,
      dashboardSettingsFeature,
    );
  }, [
    activeTab,
    colorPalette,
    configuration,
    dashboardSettingsFeature,
    enrollmentsSummary,
    operationalFacts,
    operationalGroupBy,
    shipmentSummary,
    deviceUtilizationSummary,
    organizationsSummary,
    orgs,
    refreshTimestamp,
    widgetResizeTimestamps,
  ]);

  useEffect(() => {
    advancedFilters
      .filter(filter => REMOVED_REPORT_FILTER_GROUPS.includes(filter.group) && filter.state)
      .forEach(filter => {
        updateAdvancedFilterState(
          {
            group: filter.group,
            value: filter.value,
            state: false,
          },
          undefined,
        );
      });
  }, [advancedFilters, updateAdvancedFilterState]);

  useEffect(() => {
    getOrgs().then(resp => {
      if (resp?.type === ORGANIZATION_LIST_RESULT) {
        setOrgs({
          loaded: true,
          data: resp.response?.data?.map(org => ({ ...org, value: org.masked_id, label: org.name })),
        });
      }
    });
    getOrgsForFilter().then(resp => {
      if (resp?.type === GET_MY_ORGS_RESULT) {
        setOrgsForFilter({
          loaded: true,
          data: resp.response?.data?.map(org => ({ ...org, value: org.masked_id, label: org.name })),
        });
      }
    });
    getOrganizationGroups().then(resp => {
      setOrgGroups(resp?.response?.data || []);
    });
    getCareProviders().then(resp => {
      setCareProviders(resp?.response ? uniqBy(resp.response, 'uuid') : []);
    });
  }, [getCareProviders, getOrganizationGroups, getOrgs, getOrgsForFilter, refreshTimestamp]);

  const calcRequestForFilters = () => {
    const filterRequest = {};
    let filteredOrgs = [];
    const advFilters = getRelevantFilters(advancedFilters).filter(
      filter => !REMOVED_REPORT_FILTER_GROUPS.includes(filter.group),
    );

    const orgFilter = advFilters.filter(filter => filter.group === 'orgId').map(filter => filter.value);

    if (advFilters?.length > 0) {
      filteredOrgs = orgFilter?.length ? orgFilter : orgs.data.map(org => org.value);

      if (filteredOrgs.length === 0) {
        return undefined;
      }
    }

    filterRequest.orgId = filteredOrgs?.length === orgs.data?.length ? undefined : filteredOrgs;
    if (!filterRequest.orgId || filterRequest.orgId?.length === 0) {
      delete filterRequest.orgId;
    }

    const orgGroupFilter = advFilters.find(filter => filter.group === 'orgGroup')?.value;
    if (orgGroupFilter) {
      filterRequest.groupId = orgGroupFilter;
    }

    const programFilter = advFilters.find(filter => filter.group === 'programAndSubprogram');
    if (programFilter) {
      const programOrSubprogram = programsAndSubprograms.find(program => program.value === programFilter?.value)?.type;
      if (programOrSubprogram === 'program') {
        filterRequest.program = programFilter.value;
      } else if (programOrSubprogram === 'subprogram') {
        filterRequest.subprogram = programFilter.value;
      }
    }

    const providerFilter = advFilters.find(filter => filter.group === 'provider')?.value;
    if (providerFilter) {
      filterRequest.provider = providerFilter;
    }

    return filterRequest;
  };

  useEffect(() => {
    const filterRequest = calcRequestForFilters();
    const advFilters = getRelevantFilters(advancedFilters).filter(
      filter => !REMOVED_REPORT_FILTER_GROUPS.includes(filter.group),
    );
    const dateFilterValue = advFilters.find(filter => filter.group === 'dateFilter')?.value;
    const endTimestamp = (() => {
      const nowInTimezone = moment().tz(effectiveTimezone);
      if (dateFilterValue === 'AllTime') return nowInTimezone.clone().endOf('day').format();
      if (dateFilterValue === 'Last48Hours') return nowInTimezone.clone().format();
      if (dateFilterValue === 'Last7Days') return nowInTimezone.clone().format();
      if (dateFilterValue === 'Last30Days') return nowInTimezone.clone().format();
      if (dateFilterValue === 'ThisMonth') return nowInTimezone.clone().endOf('month').format();
      if (dateFilterValue === 'LastMonth') return nowInTimezone.clone().subtract(1, 'month').endOf('month').format();
      if (dateFilterValue === 'Last2Months') return nowInTimezone.clone().endOf('month').format();
      if (dateFilterValue === 'Last6Months') return nowInTimezone.clone().subtract(1, 'month').endOf('month').format();
      if (dateFilterValue === 'Last12Months') return nowInTimezone.clone().subtract(1, 'month').endOf('month').format();
      if (dateFilterValue === 'ThisYear') return nowInTimezone.clone().endOf('month').format();

      const dates = DateFilters[dateFilterValue];
      return (
        dates?.dates?.endDate?.() ||
        nowInTimezone
          .clone()
          .endOf('day')
          .format()
      );
    })();
    const baseRequest = filterRequest || {};
    const requestKey = {
      ...baseRequest,
      endTimestamp,
      enrollmentsBaseRange,
      enrollmentsPeriodsCount,
      timezone: effectiveTimezone,
    };
    const enrollmentsSummaryRequest = {
      ...baseRequest,
      baseRange: enrollmentsBaseRange,
      periodsCount: enrollmentsPeriodsCount,
      endTimestamp,
      timezone: effectiveTimezone,
    };
    const shipmentSummaryRequest = {
      ...baseRequest,
      baseRange: enrollmentsBaseRange,
      periodsCount: enrollmentsPeriodsCount,
      endTimestamp,
      timezone: effectiveTimezone,
    };
    const organizationsSummaryRequest = {
      ...baseRequest,
      baseRange: enrollmentsBaseRange,
      periodsCount: enrollmentsPeriodsCount,
      endTimestamp,
      timezone: effectiveTimezone,
    };
    const deviceUtilizationSummaryRequest = {
      ...baseRequest,
      baseRange: enrollmentsBaseRange,
      periodsCount: enrollmentsPeriodsCount,
      endTimestamp,
      timezone: effectiveTimezone,
    };

    if (filterRequest === undefined) {
      request.current = undefined;
      setEnrollmentsSummary({ loaded: true, data: {} });
      setShipmentSummary({ loaded: true, data: {} });
      setDeviceUtilizationSummary({ loaded: true, data: {} });
      setOrganizationsSummary({ loaded: true, data: {} });
      return;
    }

    if (isEqual(requestKey, request.current)) {
      return;
    }
    setEnrollmentsSummary({ loaded: false, data: {} });
    setShipmentSummary({ loaded: false, data: {} });
    setDeviceUtilizationSummary({ loaded: false, data: {} });
    setOrganizationsSummary({ loaded: false, data: {} });

    request.current = requestKey;

    const fetchDataSequentially = async () => {
      const { cachedDispatch } = await import('../../../utils/apiCache');

      cachedDispatch(
        'enrollments_summary',
        enrollmentsSummaryRequest,
        () => getEnrollmentsSummary(enrollmentsSummaryRequest),
        60000,
      ).then(resp => {
        setEnrollmentsSummary({ loaded: true, data: resp.response });
      });

      cachedDispatch(
        'organizations_summary',
        organizationsSummaryRequest,
        () => getOrganizationsSummary(organizationsSummaryRequest),
        60000,
      ).then(resp => {
        setOrganizationsSummary({ loaded: true, data: resp.response });
      });

      cachedDispatch(
        'shipment_summary',
        shipmentSummaryRequest,
        () => getShipmentSummary(shipmentSummaryRequest),
        60000,
      ).then(resp => {
        setShipmentSummary({ loaded: true, data: resp.response });
      });

      cachedDispatch(
        'device_utilization_summary',
        deviceUtilizationSummaryRequest,
        () => getDeviceUtilizationSummary(deviceUtilizationSummaryRequest),
        60000,
      ).then(resp => {
        setDeviceUtilizationSummary({ loaded: true, data: resp.response });
      });
    };

    fetchDataSequentially();
  }, [
    advancedFilters,
    enrollmentsBaseRange,
    enrollmentsPeriodsCount,
    getEnrollmentsSummary,
    getDeviceUtilizationSummary,
    getOrganizationsSummary,
    getShipmentSummary,
    refreshTimestamp,
    effectiveTimezone,
  ]);

  useEffect(() => {
    if (activeTab !== HIGHLIGHTS_TAB && activeTab !== PERFORMANCE_TAB) {
      return;
    }

    const filterRequest = calcRequestForFilters();
    if (filterRequest === undefined) {
      setOperationalFacts({
        loaded: true,
        data: {
          patients: [],
          transitions: [],
          shipments: [],
          usage: [],
        },
      });
      return;
    }

    const alignedPeriodStart = getOperationalPeriodStart(
      operationalBaseRange,
      moment(operationalPeriodStart),
      effectiveTimezone,
    ).format();
    const operationalDatasetRequest = {
      ...(filterRequest || {}),
      baseRange: operationalBaseRange,
      periodStart: alignedPeriodStart,
      timezone: effectiveTimezone,
    };
    const nextRequestKey = JSON.stringify(operationalDatasetRequest);

    if (nextRequestKey === operationalRequest.current) {
      return;
    }
    operationalRequest.current = nextRequestKey;

    setOperationalFacts({ loaded: false, data: {} });

    const fetchOperationalFacts = async () => {
      const { cachedDispatch } = await import('../../../utils/apiCache');
      const datasetResponse = await cachedDispatch(
        'operational_dataset',
        operationalDatasetRequest,
        () => getOperationalDataset(operationalDatasetRequest),
        60000,
      );
      setOperationalFacts({
        loaded: true,
        data: stripOrganizationGroupsFromOperationalDataset(datasetResponse?.response),
      });
    };

    fetchOperationalFacts();
  }, [
    activeTab,
    advancedFilters,
    getOperationalDataset,
    operationalBaseRange,
    operationalPeriodStart,
    refreshTimestamp,
    effectiveTimezone,
  ]);

  const onRefresh = () => {
    setRefreshEnable(false);
    setTimeout(() => {
      setRefreshEnable(true);
    }, 10000);
    request.current = undefined;
    ['enrollments_summary', 'shipment_summary', 'organizations_summary', 'device_utilization_summary']
      .forEach(endpoint => apiCache.invalidate(endpoint));
    setRefreshTimestamp(now());
  };

  const layouts =
    activeTab === OVERALL_TAB
      ? getOverallDefaultLayouts({ skipPermissionCheck: isSuperUser() })
      : activeTab === HIGHLIGHTS_TAB
      ? getHighlightsDefaultLayouts({ skipPermissionCheck: isSuperUser() })
      : activeTab === PERFORMANCE_TAB
      ? getPerformanceDefaultLayouts({ skipPermissionCheck: isSuperUser() })
      : activeTab === ORGANIZATIONS_TAB
      ? getOrganizationsDefaultLayouts({ skipPermissionCheck: isSuperUser() })
      : activeTab === DEVICE_UTILIZATION_TAB
      ? getDeviceUtilizationDefaultLayouts({ skipPermissionCheck: isSuperUser() })
      : activeTab === OPERATIONAL_TAB
          ? getShipmentsDefaultLayouts({ skipPermissionCheck: isSuperUser() })
          : getEnrollmentsDefaultLayouts({ skipPermissionCheck: isSuperUser() });

  const onResize = _.debounce((layoutsData, oldItem, newItem) => {
    if (configuration?.[newItem.i]?.chartType?.value === 'table') {
      setWidgetResizeTimestamps(prev => ({ ...prev, [newItem.i]: moment() }));
    }
  }, 30);

  const renderDashboard = dashboardId =>
    widgets.length > 0 &&
    !_.isEmpty(layouts) && (
      <OverlayScrollbarsComponent
        defer
        className="scrollbar-right-margin"
        options={{ scrollbars: { autoHide: 'never', autoHideDelay: '100', theme: 'os-theme-sidebar' } }}
      >
        <DashboardLayout
          dashboardId={dashboardId}
          widgets={widgets}
          defaultLayouts={layouts}
          additionalData={{ skipPermissionCheck: isSuperUser() }}
          rowHeight={45}
          breakpoints={{ l: 1500, m: 1200, s: 800 }}
          onResize={onResize}
        />
      </OverlayScrollbarsComponent>
    );

  const renderHighlightsDashboard = () => (
    <>
      <OperationalReportTab
        baseRange={operationalBaseRange}
        groupBy={operationalGroupBy}
        periodStart={operationalFacts?.data?.meta?.period_start || operationalPeriodStart}
        onBaseRangeChange={nextBaseRange => {
          setOperationalBaseRange(nextBaseRange);
          setOperationalPeriodStart(
            getOperationalPeriodStart(
              nextBaseRange,
              moment(operationalFacts?.data?.meta?.period_start || operationalPeriodStart),
              effectiveTimezone,
            ).format(),
          );
        }}
        onGroupByChange={setOperationalGroupBy}
        onShiftPeriod={direction => {
          const meta = operationalFacts?.data?.meta;
          const nextStart = direction < 0 ? meta?.previous_period_start : meta?.next_period_start;
          setOperationalPeriodStart(
            nextStart ||
              getOperationalPeriodStart(
                operationalBaseRange,
                moment(operationalFacts?.data?.meta?.period_start || operationalPeriodStart).add(
                  direction,
                  operationalBaseRange,
                ),
                effectiveTimezone,
              ).format(),
          );
        }}
      />
      {renderDashboard(HIGHLIGHTS_DASHBOARD_ID)}
    </>
  );

  const renderPerformanceDashboard = () => (
    <>
      <OperationalReportTab
        baseRange={operationalBaseRange}
        groupBy={operationalGroupBy}
        periodStart={operationalFacts?.data?.meta?.period_start || operationalPeriodStart}
        onBaseRangeChange={nextBaseRange => {
          setOperationalBaseRange(nextBaseRange);
          setOperationalPeriodStart(
            getOperationalPeriodStart(
              nextBaseRange,
              moment(operationalFacts?.data?.meta?.period_start || operationalPeriodStart),
              effectiveTimezone,
            ).format(),
          );
        }}
        onGroupByChange={setOperationalGroupBy}
        onShiftPeriod={direction => {
          const meta = operationalFacts?.data?.meta;
          const nextStart = direction < 0 ? meta?.previous_period_start : meta?.next_period_start;
          setOperationalPeriodStart(
            nextStart ||
              getOperationalPeriodStart(
                operationalBaseRange,
                moment(operationalFacts?.data?.meta?.period_start || operationalPeriodStart).add(
                  direction,
                  operationalBaseRange,
                ),
                effectiveTimezone,
              ).format(),
          );
        }}
      />
      {renderDashboard(PERFORMANCE_DASHBOARD_ID)}
    </>
  );

  return (
    <>
      <PageHeader
        isBlack
        left={Strings.businessReport}
        right={(
          <>
            <AdvancedFilters>
              <Filter definition={getFiltersDefinition().programAndSubprogram} />
              {orgsForFilter.data?.length > 0 && <Filter definition={OrgFilter(orgsForFilter.data).orgId} />}
              {orgGroups?.length > 0 && <Filter definition={OrgGroupFilter(orgGroups).orgGroup} />}
              {careProviders?.length > 0 && <Filter definition={CareProviderFilter(careProviders).provider} />}
            </AdvancedFilters>
            {(activeTab === OVERALL_TAB || activeTab === ORGANIZATIONS_TAB || activeTab === ENROLLMENTS_TAB || activeTab === OPERATIONAL_TAB || activeTab === DEVICE_UTILIZATION_TAB) && (
              <div className="report-summary-controls">
                <Select
                  id="enrollments-base-range"
                  label="Base range"
                  className="report-summary-select"
                  data={ENROLLMENTS_BASE_RANGE_OPTIONS}
                  value={enrollmentsBaseRange}
                  onChange={option => {
                    setEnrollmentsBaseRange(option.value);
                    const nextOptions = ENROLLMENTS_PERIOD_COUNT_OPTIONS[option.value] || [];
                    if (!nextOptions.includes(enrollmentsPeriodsCount)) {
                      setEnrollmentsPeriodsCount(nextOptions[0]);
                    }
                  }}
                />
                <Select
                  id="enrollments-period-count"
                  label="Periods count"
                  className="report-summary-select"
                  data={enrollmentPeriodsCountOptions}
                  value={enrollmentsPeriodsCount}
                  onChange={option => setEnrollmentsPeriodsCount(option.value)}
                />
              </div>
            )}
            <DashboardEditModeButton dashboardId={currentDashboardId} />
            {dashboardSettingsFeature && (
              <div className="settings-button">
                <Button class="settings-button-icon" onClick={() => configDashboard(currentDashboardId)}></Button>
              </div>
            )}
            <DashboardFilter
              dashboardId={currentDashboardId}
              additionalData={{
                skipPermissionCheck: isSuperUser(),
              }}
            />
            {downloadActions}
            <Button class="refresh" disabled={!refreshEnable} onClick={onRefresh} />
          </>
        )}
      />
      <AdvancedFiltersBar
        customFilters={{ ...OrgFilter(orgsForFilter.data), ...OrgGroupFilter(orgGroups), ...CareProviderFilter(careProviders) }}
      />
      <Tabs
        defaultIndex={0}
        containerClassName="report-dashboard-tabs"
        onChange={index => setActiveTab(REPORT_TAB_ORDER[index] || OPERATIONAL_TAB)}
      >
        <Tab key={OVERALL_TAB} label="Overall">
          {renderDashboard(OVERALL_DASHBOARD_ID)}
        </Tab>
        <Tab key={PERFORMANCE_TAB} label="Performance">
          {renderPerformanceDashboard()}
        </Tab>
        <Tab key={OPERATIONAL_TAB} label="Operational">
          {renderDashboard(OPERATIONAL_DASHBOARD_ID)}
        </Tab>
        <Tab key={HIGHLIGHTS_TAB} label="Highlights">
          {renderHighlightsDashboard()}
        </Tab>
        <Tab key={ENROLLMENTS_TAB} label="Patients/Enrollments trends">
          {renderDashboard(ENROLLMENTS_DASHBOARD_ID)}
        </Tab>
        <Tab key={DEVICE_UTILIZATION_TAB} label="Device utilization">
          {renderDashboard(DEVICE_UTILIZATION_DASHBOARD_ID)}
        </Tab>
        <Tab key={ORGANIZATIONS_TAB} label="Organizations">
          {renderDashboard(ORGANIZATIONS_DASHBOARD_ID)}
        </Tab>
      </Tabs>
    </>
  );
}

ReportDashboard.propTypes = {
  getOrgs: PropTypes.func.isRequired,
  getOrgsForFilter: PropTypes.func.isRequired,
  getEnrollmentsSummary: PropTypes.func.isRequired,
  getOrganizationsSummary: PropTypes.func.isRequired,
  getShipmentSummary: PropTypes.func.isRequired,
  getDeviceUtilizationSummary: PropTypes.func.isRequired,
  getOperationalDataset: PropTypes.func.isRequired,
  downloadActions: PropTypes.node,
  dashboardSettingsFeature: PropTypes.bool,
  colorPalette: PropTypes.object,
  advancedFilters: PropTypes.array,
  configDashboard: PropTypes.func,
  dashboardConfigurations: PropTypes.object,
  getOrganizationGroups: PropTypes.func.isRequired,
  getCareProviders: PropTypes.func.isRequired,
  updateAdvancedFilterState: PropTypes.func.isRequired,
  timezone: PropTypes.string,
  replacePath: PropTypes.func.isRequired,
};

const mapStateToProps = state => {
  const labFeatures = state.labState.features;
  return {
    dashboardConfigurations: state.dashboardState.configuration,
    colorPalette:
      ColorPalette[state.dashboardState.configuration?.allDashboards?.colorPalette] || Object.values(ColorPalette)[0],
    dashboardSettingsFeature: labFeatures.find(feature => feature.name === 'Dashboard configuration')?.selected,
    advancedFilters: state.entities.advancedFilters.filters.items,
    timezone: state.auth?.profile?.preferences?.timezone || state.auth?.preferences?.timezone,
  };
};

const mapDispatchToProps = dispatch => ({
  getEnrollmentsSummary: request => dispatch(actions.getEnrollmentsSummary(request)),
  getOrganizationsSummary: request => dispatch(actions.getOrganizationsSummary(request)),
  getShipmentSummary: request => dispatch(actions.getShipmentSummary(request)),
  getDeviceUtilizationSummary: request => dispatch(actions.getDeviceUtilizationSummary(request)),
  getOperationalDataset: request => dispatch(actions.getOperationalDataset(request)),
  getOrgs: request => dispatch(orgActions.getOrgs(request)),
  getOrgsForFilter: request => dispatch(orgFilterActions.getMyOrgs(request)),
  getOrganizationGroups: pageRequest => dispatch(orgActions.getOrganizationGroups(pageRequest)),
  getCareProviders: request => dispatch(orgActions.getCareProviders(request)),
  updateAdvancedFilterState: (data, filterType) => dispatch(actionsAdvancedFilters.updateFilterState(data, filterType)),
  configDashboard: dashboardId =>
    dispatch(openModalAction('dashboard-configuration', { dashboardId, additionalData: true })),
  replacePath: url => dispatch(replace(url)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ReportDashboard);
